import { updateDevice } from '../helper/deviceHelper'
import { useAuthContext } from '../hooks/useAuthContext'
import React from 'react'
import { Device } from '../context/DeviceContext'
import { ManualControls } from '../context/SystemContext'
import { Button } from '@/components/ui/button'

type OutletRole = { outlet: number; role: string; status: string }

type OutletProps = {
    outlet: Device
    manualControls: ManualControls[]
    setManualControls: React.Dispatch<React.SetStateAction<ManualControls[]>>
}

const Outlet = ({ outlet, manualControls: _manualControls, setManualControls }: OutletProps) => {
    const { user } = useAuthContext()
    const roles = (Array.isArray(outlet.roles) ? outlet.roles : []) as OutletRole[]

    const syncManualOutlet = (role: OutletRole, status: string) => {
        setManualControls((prev) => {
            const idx = prev.findIndex(
                (c) =>
                    'outlet' in c &&
                    c.device_id === outlet.device_id &&
                    (c as { device_type?: string }).device_type === 'outlet' &&
                    (c as { outlet: number }).outlet === role.outlet
            )
            const entry = {
                device_id: outlet.device_id,
                device_type: 'outlet',
                outlet: role.outlet,
                status,
            } as ManualControls
            if (idx === -1) return [...prev, entry]
            const next = [...prev]
            next[idx] = { ...(next[idx] as object), ...entry } as ManualControls
            return next
        })
    }

    return (
        <div className="rounded-lg border border-zinc-700/90 bg-zinc-950/55 p-4 shadow-sm">
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2 border-b border-zinc-800 pb-2">
                <h4 className="text-sm font-semibold text-zinc-100">{outlet.name}</h4>
                <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">Outlet</span>
            </div>
            {roles.length === 0 ? (
                <p className="text-sm text-zinc-500">No outlet roles configured for this device.</p>
            ) : (
                <ul className="space-y-3">
                    {roles.map((role) => (
                        <li
                            key={`${outlet.device_id}-${role.outlet}`}
                            className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                        >
                            <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                                    {role.role}
                                </p>
                                <p className="text-sm text-zinc-400">
                                    State:{' '}
                                    <span className="font-medium text-zinc-100">{role.status}</span>
                                </p>
                            </div>
                            <Button
                                type="button"
                                size="sm"
                                variant={role.status === 'on' ? 'secondary' : 'default'}
                                className={
                                    role.status === 'on'
                                        ? 'border-zinc-600 bg-zinc-800 text-zinc-100 hover:bg-zinc-700'
                                        : 'bg-emerald-600 text-white hover:bg-emerald-500'
                                }
                                onClick={async (e) => {
                                    e.preventDefault()
                                    const nextStatus = role.status === 'on' ? 'off' : 'on'
                                    role.status = nextStatus
                                    syncManualOutlet(role, nextStatus)
                                    if (user !== null) await updateDevice(user, outlet)
                                }}
                            >
                                Turn {role.status === 'on' ? 'off' : 'on'}
                            </Button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}

export default Outlet
